import React from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Title,
    LineController,
    LineElement,
    PointElement,
} from 'chart.js';
import { WindowManager } from '@shared/ui-react/FloatingWindow';
import { Measurement } from '../../../../../../typings/reality-capture/volumes/models';

ChartJS.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Title,
    LineController,
    LineElement,
    PointElement,
);

export function openChartWindow(measurement: Measurement): void {
    WindowManager.open({
        group: 'chart-tools',
        id: 'chart-tool',
        title: __('js_map_measuring_tools.volume_chart'),
        showCloseButton: true,
        resize: 'both',
        defaultSizing: {
            width: window.innerWidth * 0.55,
            height: 400 + window.innerHeight * 0.2,
            x: 40,
            y: window.innerHeight * 0.4,
        },
        body: <ChartDialog measurement={measurement} />,
        onClose: () => closeChartWindow(),
    });
}

export function closeChartWindow(): void {
    WindowManager.close('chart-tool');
}

const ChartDialog = ({ measurement }: { measurement: Measurement }): React.ReactElement => {
    if (measurement.alignmentSegments && measurement.alignmentSegments.length > 0) {
        return <AlignmentLineChart measurement={measurement} />;
    }
    return <SummaryBarChart measurement={measurement} />;
};

/** Line chart showing fill and cut per 10 m chainage section along an alignment. */
const AlignmentLineChart = ({ measurement }: { measurement: Measurement }): React.ReactElement => {
    const segments = measurement.alignmentSegments!;
    const labels = segments.map(s => String(s.station));

    const datasets = [
        {
            label: __('js_map_measuring_tools.fill') + ' (m³)',
            data: segments.map(s => Number(s.fillVolume.toFixed(2))),
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 0.15)',
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.3,
            fill: false,
        },
        {
            label: __('js_map_measuring_tools.cut') + ' (m³)',
            data: segments.map(s => Number(s.cutVolume.toFixed(2))),
            borderColor: 'rgba(255, 99, 132, 1)',
            backgroundColor: 'rgba(255, 99, 132, 0.15)',
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.3,
            fill: false,
        },
    ];

    const data = { labels, datasets };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right' as const },
            tooltip: { enabled: true },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: __('js_map_measuring_tools.station') || 'Station (m)',
                },
                ticks: { autoSkip: true, maxTicksLimit: 25 },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: __('js_map_measuring_tools.chart_volume') + ' (m³)',
                },
            },
        },
    };

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <Line data={data} options={options} />
        </div>
    );
};

/** Simple bar chart summary for a single non-alignment measurement. */
const SummaryBarChart = ({ measurement }: { measurement: Measurement }): React.ReactElement => {
    const labels = [
        __('js_map_measuring_tools.fill') + ' (m³)',
        __('js_map_measuring_tools.cut') + ' (m³)',
        __('js_map_measuring_tools.total') + ' (m³)',
    ];

    const datasets = [
        {
            label: measurement.name ?? `#${measurement.id ?? 1}`,
            data: [
                Number(measurement.fill_volume.toFixed(2)),
                Number(measurement.cut_volume.toFixed(2)),
                Number((measurement.fill_volume + measurement.cut_volume).toFixed(2)),
            ],
            backgroundColor: [
                'rgba(54, 162, 235, 0.6)',
                'rgba(255, 99, 132, 0.6)',
                'rgba(16, 185, 129, 0.6)',
            ],
            borderColor: [
                'rgba(54, 162, 235, 1)',
                'rgba(255, 99, 132, 1)',
                'rgba(16, 185, 129, 1)',
            ],
            borderWidth: 1,
        },
    ];

    const data = { labels, datasets };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: __('js_map_measuring_tools.chart_volume') + ' (m³)',
                },
            },
        },
    };

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <Bar data={data} options={options} />
        </div>
    );
};
